
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Clock, Users, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import MFODetailDialog from "../mfo/MFODetailDialog";
import { MFOItem } from "@/types/kanban";

export type MFOPriority = "high" | "medium" | "low";

export type MFOStatus = "not-started" | "in-progress" | "delayed" | "completed";

export interface MFO {
  id: string;
  title: string;
  description: string;
  progress: number;
  dueDate: string;
  priority: MFOPriority;
  status: MFOStatus;
  stakeholders: {
    id: string;
    name: string;
    avatar?: string;
  }[];
}

interface OngoingMFOCardProps {
  mfo: MFO;
}

const getStatusColor = (status: MFOStatus) => {
  switch (status) {
    case "not-started":
      return "bg-muted text-foreground";
    case "in-progress":
      return "bg-blue-100 text-blue-800";
    case "delayed":
      return "bg-amber-100 text-amber-800";
    case "completed":
      return "bg-green-100 text-green-800";
    default:
      return "bg-muted text-foreground";
  }
};

const getPriorityColor = (priority: MFOPriority) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-800";
    case "medium":
      return "bg-amber-100 text-amber-800";
    case "low":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-muted text-foreground";
  }
};

const getProgressColor = (progress: number) => {
  if (progress < 30) return "bg-red-500";
  if (progress < 70) return "bg-amber-500";
  return "bg-green-500";
}

const OngoingMFOCard = ({ mfo }: OngoingMFOCardProps) => {
  const [isMFODialogOpen, setIsMFODialogOpen] = useState(false);
  const navigate = useNavigate();
  
  // Handle potentially invalid date format
  const formattedDate = (() => {
    try {
      return new Date(mfo.dueDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return mfo.dueDate; // Fallback to the raw string if parsing fails
    }
  })();

  const mfoItem: MFOItem = {
    id: mfo.id,
    code: mfo.id.substring(0, 7).toUpperCase(),
    title: mfo.title,
    successIndicators: [mfo.description],
    budget: 1000000, // This will be populated from real data
    timeline: { q1: true, q2: true, q3: false, q4: false }, // This will be populated from real data
    collaborators: mfo.stakeholders.map(s => ({ id: s.id, name: s.name, avatar: s.avatar })),
    departmentId: "dept-tourism", // This will be populated from real data
    status: mfo.status,
    priority: mfo.priority,
    progress: mfo.progress,
    dueDate: formattedDate
  };

  const handleViewMFO = () => {
    navigate(`/dashboard/mfo/${mfo.id}`);
  };

  return (
    <>
      <Card className="overflow-hidden transition-all hover:shadow-md animate-scale">
        <CardContent className="p-5">
          <div className="flex justify-between items-start mb-3">
            <div className="space-y-1">
              <h3 className="font-medium text-base">{mfo.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {mfo.description}
              </p>
            </div>
            <div className="flex space-x-2">
              <Badge variant="outline" className={getPriorityColor(mfo.priority)}>
                {mfo.priority}
              </Badge>
              <Badge variant="outline" className={getStatusColor(mfo.status)}>
                {mfo.status.replace("-", " ")}
              </Badge>
            </div>
          </div>

          <div className="space-y-4 mt-4">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{mfo.progress}%</span>
              </div>
              <Progress value={mfo.progress} className={getProgressColor(mfo.progress)} />
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center text-sm text-muted-foreground">
                <CalendarDays size={14} className="mr-1.5" />
                <span>{formattedDate}</span>
              </div>
              <div className="flex -space-x-2">
                {mfo.stakeholders.slice(0, 3).map((stakeholder) => (
                  <Avatar key={stakeholder.id} className="h-6 w-6 border-2 border-background">
                    <AvatarImage src={stakeholder.avatar} alt={stakeholder.name} />
                    <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                      {stakeholder.name.split(" ").map(n => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {mfo.stakeholders.length > 3 && (
                  <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-background">
                    +{mfo.stakeholders.length - 3}
                  </div>
                )}
              </div>
            </div>

            <div className="pt-2 mt-2 border-t flex justify-end">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs"
                onClick={handleViewMFO}
              >
                View MFO <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <MFODetailDialog 
        mfo={mfoItem} 
        isOpen={isMFODialogOpen} 
        onOpenChange={setIsMFODialogOpen} 
      />
    </>
  );
};

export default OngoingMFOCard;
